
import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import {
	ObjectType,
	Platform,
	SportCompetitorV2,
	SportEventStatus,
	SportType,
	SraPlacement,
} from '@/@types/graphql-types';
import { BreadcrumbItem, SportPageType } from '@/interfaces/sport-graphql';

import Breadcrumbs from '@/components/sports/common/Breadcrumbs.vue';
import CompetitorLogo from '@/components/sports/common/CompetitorLogo.vue';
import SingleStepEventEncounter from '@/components/sports/common/SingleStepEventEncounter.vue';
import SingleStepEventEncounterList from '@/components/sports/common/SingleStepEventEncounterList.vue';
import Header from '@/components/sports/common/Header.vue';
import SportsLayout from '@/components/sports/common/SportsLayout.vue';
import EventEncounterLarge from '@/components/sports/common/team/EventEncounterLarge.vue';
import TeamCompetitionsCalendar from '@/components/sports/common/team/TeamCompetitionsCalendar.vue';
import ProvideUpcomingSportEvents from '@/components/sports/single-step/ProvideUpcomingSportEvents.vue';

import { isMultiStepSport, SportLocale } from '@/enums/sports';
import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { getSportPageContext, SingleStepTeamContext } from '@/helpers/sports/config';

import { SnowplowContext, SnowplowSportContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { LocaleResponse } from '@/helpers/locale-helper';
import {
	GetSingleStepTeamDocument,
	GetSingleStepTeamQuery,
	GetSingleStepTeamQueryVariables,
} from '@/pages/graphql/queries/GetSingleStepTeam.query';

const Language = namespace('language');
const Meta = namespace('meta');

@Component({
	name: 'SingleStepTeamPage',
	components: {
		Breadcrumbs,
		CompetitorLogo,
		SingleStepEventEncounterList,
		Header,
		EventEncounterLarge,
		ProvideUpcomingSportEvents,
		SportsLayout,
		TeamCompetitionsCalendar,
		SingleStepEventEncounter,
	},
	apollo: {
		singleStepTeam: {
			fetchPolicy: 'cache-and-network',
			query: GetSingleStepTeamDocument,
			variables(): GetSingleStepTeamQueryVariables {
				return this.sportEventsQueryVariables;
			},
			update(data: GetSingleStepTeamQuery) {
				return data.urlV2.node;
			},
			result(_result, _key) {
				this.SET_JSON_LD(this.schemaData);
			},
			skip() {
				if (this.$route.name != null && this.$route.name.includes('team')) return false;
				return true;
			},
		},
	},
})
export default class SingleStepTeamPage extends Vue {
	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.Getter localeObject: LocaleResponse;
	@Language.State webLocale: SportLocale;

	@Meta.Mutation SET_JSON_LD: (content: string) => void;

	selectedCompetitionsIds: string[] = [];
	selectedProviderShortnames: string[] = [];

	singleStepTeam: SportCompetitorV2 | undefined;

	competitionColumns = [];

	activated() {
		// note: all sport pages are excluded from automatic page tracking
		/**
		 * setTimeout added here to prevent pageView to be triggered before link click track event.
		 * that's because when adding the @click.native to router-link the track event will get triggered
		 * after the navigation which causes the pageView to get triggered first.
		 *
		 */
		setTimeout(() => {
			TrackingHelper.trackPage(this.$route.path, [this.sportContext]);
		});
	}

	get SraPlacement() {
		return SraPlacement;
	}

	get competitionEvents() {
		return this.singleStepTeam?.sportEventBucketsV2?.edges ?? [];
	}

	get teamName() {
		return this.singleStepTeam?.content?.name ?? '';
	}

	get startAt() {
		return {
			from: shiftDateWithTimezone(this.localeObject.timezone).toISOString(),
		};
	}

	get sportEventsQueryVariables(): GetSingleStepTeamQueryVariables {
		return {
			fullPath: this.$route.path,
			language: this.language,
			country: this.country,
			platform: Platform.Web,
			filter: {
				startAt: this.startAt,
				packages: this.selectedProviderShortnames,
				competitionIds: this.selectedCompetitionsIds,
			},
			first: 3, // number of competitions to get events for (competition filtering fetches the others)
			bucketSize: 10,
			timezoneOffset: 0,
		};
	}

	get breadcrumbs(): BreadcrumbItem[] {
		return [
			// e.g. Sport
			{ name: this.$tc('WEBAPP_SPORTS'), routeName: 'app.sports.all' },

			// e.g. Fussball
			{
				name: this.$tc(this.context.sportName),
				objectType: SportPageType.SportTeam,
				url: this.context.getOverviewUrl(this.webLocale),
			},

			// e.g. Bayern Munich
			{
				name: this.teamName,
				objectType: ObjectType.SportCompetition,
				objectId: this.singleStepTeam?.objectId,
				url: this.$route.fullPath,
			},
		];
	}

	get schemaCompetitions() {
		const competitions =
			this.competitionColumns.map((competition: any) => {
				return competition?.competition?.content?.name;
			}) || [];

		const uniqueCompetitions = competitions.filter((el, index) => {
			return competitions.indexOf(el) === index;
		});

		return uniqueCompetitions.map((competition: any) => {
			return {
				'@type': 'SportsOrganization',
				name: competition,
			};
		});
	}

	get schemaEvents() {
		const events = this.singleStepTeam?.sportEventBucketsV2?.edges?.[0]?.node?.edges || [];

		return events.map((event: any) => {
			const eventData = event.node;

			const { key, params } = this.context.getEventTitle(eventData, this.webLocale);
			const eventTitle = this.$t(key, params);

			const prices =
				eventData?.offers?.map((offer: any) => offer.retailPrice as number).filter((v: any) => v != null) ?? [];

			let eventStatus = '';
			switch (eventData.content?.status) {
				case SportEventStatus.Postponed:
					eventStatus = 'EventPostponed';
					break;
				case SportEventStatus.Cancelled:
					eventStatus = 'EventCancelled';
					break;
				default:
					eventStatus = 'EventScheduled';
			}

			return {
				'@context': 'http://schema.org',
				'@type': 'BroadcastEvent',
				name:
					`${eventTitle} ${eventData?.competition?.content.name} ${eventData.content?.season?.name ?? ''}` ??
					'',
				isLiveBroadcast: (eventData?.offers ?? []).length > 0,
				startDate: eventData.content?.startAt ?? '',
				endDate: eventData.content?.endAt ?? '',
				broadcastOfEvent: {
					'@type': 'SportsEvent',
					sport: this.context.sportType.toLowerCase().replace(/_/g, ' '),
					organizer: {
						'@type': 'Organization',
						name: eventData?.competition?.content.name ?? '',
					},
					eventStatus,
					name:
						`${eventTitle} ${eventData?.competition?.content.name} ${
							eventData.content?.season?.name ?? ''
						}` ?? '',
					awayTeam: {
						'@type': 'SportsTeam',
						name:
							eventData?.competitors?.awayTeam?.competitors
								.map((c: any) => c.content.name)
								.find(Boolean) ?? '',
						sport: this.context.sportType.toLowerCase().replace(/_/g, ' '),
					},
					homeTeam: {
						'@type': 'SportsTeam',
						name:
							eventData?.competitors?.homeTeam?.competitors
								.map((c: any) => c.content.name)
								.find(Boolean) ?? '',
						sport: this.context.sportType.toLowerCase().replace(/_/g, ' '),
					},
					startDate: eventData.content?.startAt ?? '',
					endDate: eventData.content?.endAt ?? '',
					location: {
						'@type': 'Place',
						name: eventData.content?.venue?.name,
					},
					offers: eventData?.offers
						? {
								'@type': 'AggregateOffer',
								highPrice: Math.max(...prices) ?? undefined,
								lowPrice: Math.min(...prices) ?? undefined,
								priceCurrency: eventData?.offers.map((offer: any) => offer.currency).find(Boolean),
								offerCount: eventData?.offers.length,
								offers: eventData?.offers.map((offer: any) => ({
									'@type': 'Offer',
									name: `${eventTitle} ${offer.presentationType} ${offer.monetizationType}`,
									price: offer.retailPrice,
									priceCurrency: offer.currency,
									url: offer.standardWebURL,
								})),
						  }
						: null,
					audience: {
						'@type': 'Audience',
						name: 'Sports Fans',
					},
				},
			};
		});
	}

	get schemaData() {
		const schemaDataObject = {
			'@context': 'http://schema.org',
			'@type': 'SportsTeam',
			name: this.teamName,
			sport: this.context.sportType.toLowerCase().replace(/_/g, ' '),
			memberOf: this.schemaCompetitions,
			events: this.schemaEvents,
		};

		return JSON.parse(JSON.stringify(schemaDataObject).replace(/&quot;/g, '"')) || '';
	}

	// TEAM PAGE CONTEXT
	context = {} as SingleStepTeamContext;

	@Watch('$route.meta.sport', { immediate: true })
	setContext(newMeta: SportType | undefined) {
		if (newMeta && newMeta !== this.context.sportType && !isMultiStepSport(newMeta)) {
			this.context = getSportPageContext({
				sport: this.$route.meta!.sport as SportType,
				pageType: SportPageType.SportTeam,
			});
		}
	}

	// TRACKING
	get sportContext() {
		const context = new SnowplowSportContext(this.context.sportType, ObjectType.SportCompetitor);
		context.competitorIdHome = this.singleStepTeam?.objectId.toString();
		return context;
	}

	trackCompetitionFilter({ target }: Event) {
		const { value, checked } = target as HTMLInputElement;
		trackCompetitionFilter(value, checked, [this.sportContext]);
	}

	trackSeeAllToggle() {
		trackSeeAllToggle([this.sportContext]);
	}
}

/* TRACKING SPECS */
// Competition Filter Chips
function trackCompetitionFilter(competitionId: string, toggleStatus: boolean, contexts: SnowplowContext[]) {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'filter_toggled',
			label: 'competition',
			property: competitionId,
			value: toggleStatus ? 1 : 0,
			nonInteraction: false,
		},
		contexts
	);
}

// Provider Filter Chips
function trackSeeAllToggle(contexts: SnowplowContext[]) {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'filter_popover_toggled',
			label: 'WEBAPP_COMPETITORS',
			property: 'sports',
			nonInteraction: false,
		},
		contexts
	);
}
